'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

import { GroqImage } from '@/helpers/Image';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { EXPO } from './reveal';

function MicrophoneIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="#fff"
      aria-hidden
      className="w-[2.5rem] h-[2.5rem]">
      <path d="M14.375 8.75V10.625C14.375 11.7853 13.9141 12.8981 13.0936 13.7186C12.2731 14.5391 11.1603 15 10 15C8.83968 15 7.72688 14.5391 6.90641 13.7186C6.08594 12.8981 5.625 11.7853 5.625 10.625V8.75H4.375V10.625C4.37566 12.0082 4.88591 13.3426 5.80826 14.3733C6.73062 15.4041 8.0004 16.0588 9.375 16.2125V17.5H6.875V18.75H13.125V17.5H10.625V16.2125C11.9996 16.0588 13.2694 15.4041 14.1917 14.3733C15.1141 13.3426 15.6243 12.0082 15.625 10.625V8.75H14.375Z" />
      <path d="M10 13.75C10.8288 13.75 11.6237 13.4208 12.2097 12.8347C12.7958 12.2487 13.125 11.4538 13.125 10.625V4.375C13.125 3.5462 12.7958 2.75134 12.2097 2.16529C11.6237 1.57924 10.8288 1.25 10 1.25C9.1712 1.25 8.37634 1.57924 7.79029 2.16529C7.20424 2.75134 6.875 3.5462 6.875 4.375V10.625C6.875 11.4538 7.20424 12.2487 7.79029 12.8347C8.37634 13.4208 9.1712 13.75 10 13.75ZM8.125 4.375C8.125 3.87772 8.32254 3.40081 8.67417 3.04917C9.02581 2.69754 9.50272 2.5 10 2.5C10.4973 2.5 10.9742 2.69754 11.3258 3.04917C11.6775 3.40081 11.875 3.87772 11.875 4.375V10.625C11.875 11.1223 11.6775 11.5992 11.3258 11.9508C10.9742 12.3025 10.4973 12.5 10 12.5C9.50272 12.5 9.02581 12.3025 8.67417 11.9508C8.32254 11.5992 8.125 11.1223 8.125 10.625V4.375Z" />
    </svg>
  );
}

function useFloat(active: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = (now - start) / 1000;
      el.style.translate = `${(Math.cos(t * 0.41 + 1.7) * 3.3).toFixed(2)}px ${(Math.sin(t * 0.59 + 1.7) * 5.5).toFixed(2)}px`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      el.style.translate = '';
    };
  }, [active]);
  return ref;
}

function Sticker({ text, play, reduce }: { text: string; play: boolean; reduce: boolean | null }) {
  const [landed, setLanded] = useState(false);
  const floatRef = useFloat(landed && !reduce);
  const drawn = play || !!reduce;

  return (
    <div
      ref={floatRef}
      className="absolute left-[4rem] top-[28.625rem] lg:left-[15.75rem] lg:top-[40.03rem]">
      <svg
        viewBox="0 0 80 44"
        fill="none"
        aria-hidden
        className="absolute overflow-visible left-[6.875rem] top-[-6.3125rem] w-[10rem] h-[5.5rem]">
        <path
          d="M0 44C4 14 50 4 80 0"
          stroke="#fff"
          strokeOpacity={0.75}
          strokeWidth={1.5}
          strokeLinecap="round"
          pathLength={1}
          strokeDasharray={1}
          style={{
            strokeDashoffset: drawn ? 0 : 1,
            transition: reduce ? 'none' : 'stroke-dashoffset 1.1s cubic-bezier(.65,0,.35,1) .35s',
          }}
        />
        <path
          d="M74.6 -4.1L80 0.15L75.9 5.35"
          stroke="#fff"
          strokeOpacity={0.75}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ opacity: drawn ? 1 : 0, transition: reduce ? 'none' : 'opacity .25s ease 1.35s' }}
        />
      </svg>
      <motion.p
        initial={reduce ? false : { opacity: 0, y: 10, scale: 0.94 }}
        animate={play ? { opacity: 1, y: 0, scale: 1 } : undefined}
        transition={{ opacity: { duration: 0.5, ease: EXPO }, default: { duration: 0.7, ease: EXPO } }}
        onAnimationComplete={() => setLanded(true)}
        className="m-0 whitespace-nowrap rounded-[0.5rem] bg-[#FFE11A] px-[1.5rem] py-[0.75rem] font-geist font-[500] text-black text-[2.25rem] leading-[3rem]">
        {text}
      </motion.p>
    </div>
  );
}

export default function AboutPhoto({ data, className }: { data: PageRizAffiliateSchema; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.12, margin: '0px 0px -6% 0px' });
  const stickerInView = useInView(ref, { once: true, amount: 0.35 });
  const [decoded, setDecoded] = useState(false);

  useEffect(() => {
    if (!inView || decoded) return;
    const timer = window.setTimeout(() => setDecoded(true), 900);
    return () => window.clearTimeout(timer);
  }, [inView, decoded]);

  if (!data?.aboutImage) return null;
  const show = inView && decoded;

  return (
    <div
      ref={ref}
      className={className}>
      <div className="w-full rounded-[1rem] border border-black/[0.16] p-[0.75rem]">
        <div className="relative overflow-clip w-full rounded-[0.5rem] aspect-[384/421] lg:aspect-[620/622.4]">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 1.02, filter: 'blur(12px)' }}
            animate={show ? { opacity: 1, scale: 1, filter: 'blur(0px)' } : undefined}
            transition={{
              opacity: { duration: 0.9, ease: 'easeInOut' },
              scale: { duration: 1.2, ease: EXPO },
              filter: { duration: 1, ease: EXPO },
            }}
            className="absolute inset-0">
            <GroqImage
              src={data.aboutImage}
              alt={data.aboutImage?.name ?? ''}
              fill
              quality={100}
              sizes="(min-width: 993px) 33vw, 92vw"
              style={{ width: '100%', height: '100%' }}
              onLoad={(event) => {
                const img = event.currentTarget;
                img.decode().then(
                  () => setDecoded(true),
                  () => setDecoded(true),
                );
              }}
              className="object-cover"
            />
          </motion.div>

          {data?.aboutSticker && (
            <Sticker
              text={data.aboutSticker}
              play={stickerInView}
              reduce={reduce}
            />
          )}

          {(data?.aboutPlayerLabel || data?.aboutPlayerTitle) && (
            <div className="absolute inset-x-0 mx-auto flex flex-col overflow-clip bottom-[1.125rem] w-[calc(100%-2.25rem)] gap-[1.5rem] rounded-[1rem] border border-white/10 bg-[#051712]/[0.88] p-[2rem] lg:bottom-[2.25rem] lg:w-[calc(100%-4.25rem)]">
              <div className="flex items-center gap-[1.5rem]">
                <span className="flex shrink-0 items-center justify-center w-[5rem] h-[5rem] rounded-[0.5rem] bg-[#296E59]">
                  <MicrophoneIcon />
                </span>
                <div className="flex min-w-0 flex-col gap-[0.5rem]">
                  {data?.aboutPlayerLabel && (
                    <p className="m-0 h-[2.25rem] font-geist font-[400] text-white/60 text-[1.75rem] leading-[2.5rem]">
                      {data.aboutPlayerLabel}
                    </p>
                  )}
                  {data?.aboutPlayerTitle && (
                    <p className="m-0 whitespace-nowrap font-geist font-[500] text-white text-[1.75rem] leading-[1.75rem] lg:text-[2rem]">
                      {data.aboutPlayerTitle}
                    </p>
                  )}
                </div>
              </div>
              <span
                aria-hidden
                className="relative block w-full overflow-clip h-[0.25rem] rounded-[0.125rem] bg-white/[0.15]">
                <span className="absolute inset-y-0 left-0 w-[20rem] rounded-[0.125rem] bg-[#CFFFDB]" />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
